import { useTranslation } from 'react-i18next';
import { Info, TrendingDown, Shield } from 'lucide-react';

export const CostSharingSection = ({ distance, seats, rideMode }) => {
  const { t } = useTranslation();

  if (!distance || distance <= 0) {
    return (
      <div className="form-section price-section" data-testid="cost-sharing-section">
        <h2><TrendingDown size={20} /> {t('createRide.costSharing.title')}</h2>
        <div className="price-placeholder">
          <Info size={24} />
          <p>{t('createRide.costSharing.enterDistanceFirst')}</p>
        </div>
      </div>
    );
  }

  // Dynamic pricing: ≤50km → 0.30€/km, >50km → 0.28€/km
  const costPerKm = distance <= 50 ? 0.30 : 0.28;
  const effectiveMode = distance <= 50 ? 'city' : 'longdistance';
  const totalCost = (Number(distance || 0) * Number(costPerKm || 0)).toFixed(2);
  const pricePerSeat = (totalCost / (Number(seats || 1) + 1)).toFixed(2);
  const driverSavings = (totalCost - pricePerSeat).toFixed(2);
  const driverOwnShare = pricePerSeat;

  return (
    <div className="form-section price-section" data-testid="cost-sharing-section">
      <h2><TrendingDown size={20} /> {t('createRide.costSharing.title')}</h2>

      {/* Pricing tier badge */}
      <div data-testid="pricing-tier-badge" style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.35rem 0.85rem', borderRadius: '20px', marginBottom: '0.75rem',
        fontSize: '0.8rem', fontWeight: 600,
        background: effectiveMode === 'city' ? '#dbeafe' : '#fef3c7',
        color: effectiveMode === 'city' ? '#1d4ed8' : '#92400e',
        border: `1px solid ${effectiveMode === 'city' ? '#93c5fd' : '#fcd34d'}`,
      }}>
        {effectiveMode === 'city' ? t('rideMode.city') : t('rideMode.longdistance')}
        <span style={{ fontWeight: 400 }}>·</span>
        {Number(costPerKm || 0).toFixed(2)} €/km
      </div>

      <div className="cost-breakdown" style={{
        background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)',
        borderRadius: '12px', padding: '1.25rem', marginBottom: '1rem',
        border: '1px solid #bbf7d0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <Shield size={18} style={{ color: '#16a34a' }} />
          <span style={{ fontWeight: 600, color: '#15803d', fontSize: '0.9rem' }}>
            {t('createRide.costSharing.fairPriceLabel')}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              {t('createRide.costSharing.totalTripCost')}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>
              {totalCost}€
            </div>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
              {distance} km × {Number(costPerKm ?? 0).toFixed(2)}€/km
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '8px', padding: '0.75rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
              {t('createRide.costSharing.pricePerPassenger')}
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#2563eb' }}>
              {pricePerSeat}€
            </div>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>
              {totalCost}€ ÷ {seats + 1} {t('createRide.costSharing.occupants')}
            </div>
          </div>
        </div>

        <div style={{ fontSize: '0.8rem', color: '#6b7280', display: 'flex', alignItems: 'flex-start', gap: '0.4rem' }}>
          <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
          <span>{t('createRide.costSharing.explainer')}</span>
        </div>
      </div>

      {/* Driver Savings Box */}
      <div data-testid="driver-savings-box" style={{
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
        borderRadius: '12px', padding: '1.25rem',
        border: '1px solid #93c5fd'
      }}>
        <div style={{ fontWeight: 600, color: '#1d4ed8', marginBottom: '0.5rem', fontSize: '0.95rem' }}>
          {t('createRide.costSharing.yourSavings')}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.5rem' }}>
          <span style={{ fontSize: '2rem', fontWeight: 800, color: '#1d4ed8' }}>
            {driverSavings}€
          </span>
          <span style={{ fontSize: '0.85rem', color: '#3b82f6' }}>
            {t('createRide.costSharing.savedDescription')}
          </span>
        </div>
        <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
          {t('createRide.costSharing.youPay')}: <strong>{driverOwnShare}€</strong> ({t('createRide.costSharing.insteadOf')} {totalCost}€)
        </div>
      </div>
    </div>
  );
};
